<script lang="ts">
import { defineComponent, PropType, ref, computed, onMounted } from 'vue'
import CUSTOM_FIELD_TYPES from '@/constants/customFieldTypes'
import TextareaForm from '@/components/form/TextareaForm.vue'
import SelectorForm from '@/components/form/SelectorForm.vue'
import CheckboxForm from '@/components/form/CheckboxForm.vue'
import CheckboxItem from '@/components/form/CheckboxItem.vue'
import MultiSelectorFormWithSecondaryItem from '@/components/form/MultiSelectorFormWithSecondaryItem.vue'
import htmlUtil from '@/utilities/html'

export default defineComponent({
  name: 'InputHearingFieldItem',

  components: {
    TextareaForm,
    SelectorForm,
    CheckboxForm,
    CheckboxItem,
    MultiSelectorFormWithSecondaryItem,
  },

  props: {
    hearingFieldConfigItem: {
      type: Object as PropType<HearingFieldConfigItem>,
      required: true,
    },

    fieldIdNameSuffix: {
      type: String,
      required: true,
    },

    userFieldIdNameSuffix: {
      type: String,
      required: true,
    },

    namespace: {
      type: String,
      default: '',
    },

    name: {
      type: String,
      default: '',
    },

    modelValue: {
      type: [String, Number, Boolean, Array] as PropType<InputValue | undefined>,
      required: true,
    },

    validationScheme: {
      type: Object as PropType<ValidationScheme>,
      default: () => ({}),
    },
  },

  emits: [
    'update:modelValue',
    'select:primary',
    'open:secondary',
    'close:secondary'
  ],

  setup(props) {
    const headerWithLink = ref('')

    const isValidFieldType = computed<boolean>(() => !!Object.values(CUSTOM_FIELD_TYPES).includes(props.hearingFieldConfigItem.fieldType))

    onMounted(() => {
      headerWithLink.value = htmlUtil.toAnchorFromUrlText(props.hearingFieldConfigItem.title, '<i class="i-window__blank"></i>', ['a'])
    })

    return {
      // constants
      CUSTOM_FIELD_TYPES,

      // data
      headerWithLink,

      // computed
      isValidFieldType,
    }
  }
})
</script>

<template>
  <div
    v-if="isValidFieldType"
    class="div">
    <!-- テキストエリア -->
    <template v-if="hearingFieldConfigItem.fieldType === CUSTOM_FIELD_TYPES.TEXT">
      <TextareaForm
        :header="headerWithLink"
        :description="hearingFieldConfigItem.description"
        :namespace="namespace"
        :name="name"
        :rows="2"
        :model-value="(modelValue as string)"
        :validation-scheme="validationScheme"
        @update:model-value="$emit('update:modelValue', $event)" />
    </template>

    <!-- ドロップダウンリスト -->
    <template v-else-if="hearingFieldConfigItem.fieldType === CUSTOM_FIELD_TYPES.SELECT_SINGLE">
      <SelectorForm
        :header="headerWithLink"
        :description="hearingFieldConfigItem.description"
        :namespace="namespace"
        :name="name"
        :model-value="(modelValue as SingleSelect)"
        :items="hearingFieldConfigItem.selectItems || []"
        default-selector-label="選択してください"
        default-selector-value=""
        :validation-scheme="validationScheme"
        @update:model-value="$emit('update:modelValue', $event)" />
    </template>

    <!-- マルチセレクター -->
    <template v-else-if="hearingFieldConfigItem.fieldType === CUSTOM_FIELD_TYPES.SELECT_MULTI">
      <MultiSelectorFormWithSecondaryItem
        :namespace="namespace"
        :model-value="(modelValue as MultiSelect)"
        :items="hearingFieldConfigItem.selectItems || []"
        :header="headerWithLink"
        :description="hearingFieldConfigItem.description"
        :name="name"
        :validation-scheme="validationScheme"
        :primary-item-values-for-expand-action="hearingFieldConfigItem.secondary?.primaryItemValues"
        @update:model-value="$emit('update:modelValue', $event)"
        @select:primary="$emit('select:primary', $event)"
        @close:secondary="$emit('close:secondary')">
        <slot name="secondary" />
      </MultiSelectorFormWithSecondaryItem>
    </template>

    <!-- チェックボックス（複数項目） -->
    <template v-else-if="hearingFieldConfigItem.fieldType === CUSTOM_FIELD_TYPES.CHECKBOX_MULTI">
      <CheckboxForm
        :header="headerWithLink"
        :description="hearingFieldConfigItem.description"
        :namespace="namespace"
        :name="name"
        :model-value="(modelValue as MultiSelect)"
        :items="hearingFieldConfigItem.selectItems || []"
        :cols="2"
        :validation-scheme="validationScheme"
        @update:model-value="$emit('update:modelValue', $event)" />
    </template>

    <!-- チェックボックス（単一項目） -->
    <template v-else-if="hearingFieldConfigItem.fieldType === CUSTOM_FIELD_TYPES.CHECKBOX">
      <CheckboxItem
        :namespace="namespace"
        :model-value="(modelValue as number)"
        :name="name"
        :value="1"
        :false-value="0"
        :label="headerWithLink"
        :validation-scheme="validationScheme"
        @update:model-value="$emit('update:modelValue', $event)" />
    </template>
    <input
      class="input"
      type="hidden"
      :name="`${hearingFieldConfigItem.name}${userFieldIdNameSuffix}`"
      :value="hearingFieldConfigItem.id">
    <input
      class="input"
      type="hidden"
      :name="`${hearingFieldConfigItem.name}${fieldIdNameSuffix}`"
      :value="hearingFieldConfigItem.hearingFieldId">
  </div>
</template>
